import * as React from 'react'

import { useAsyncData } from 'modules/core/hooks/useAsyncData'

import type { AnalyticsFilterForm } from 'modules/reviews/hooks/useAnalyticsMultiFilters'
import { AnalyticsFilters } from 'modules/reviews/lib/AnalyticsFilters'
import { AnalyticResults } from 'modules/reviews/store/collections/analyticResults'
import { QuestionCategories } from 'modules/reviews/store/collections/questionCategories'
import { QuestionGroups } from 'modules/reviews/store/collections/questionGroups'
import type AnalyticResult from 'modules/reviews/store/models/AnalyticResult'
import type { Cycle } from 'modules/reviews/store/models/AnalyticResult'
import type ReviewForm from 'modules/reviews/store/models/ReviewForm'
import type Review from 'modules/reviews/store/models/v2/Review'

const analyticsContext = React.createContext<Values | undefined>(undefined)

class DataParams {
  [key: string]: number | number[] | string | undefined
}
interface Props {
  form: ReviewForm | Review
  revieweeId?: string
}
interface Values {
  loading: boolean
  form: ReviewForm | Review
  analyticResults: AnalyticResults
  analyticResult: AnalyticResult | undefined | null
  questionGroups: QuestionGroups
  questionCategories: QuestionCategories
  cycle: Cycle | null
  setCycle: (cycle: Cycle) => void
  reviewAnalyticsFilter: AnalyticsFilterForm | undefined
  setReviewAnalyticsFilter: (reviewAnalyticsFilter: AnalyticsFilterForm) => void
}

export const ReviewAnalyticsProvider = ({
  children,
  form,
  revieweeId,
}: React.PropsWithChildren<Props>) => {
  const formId = form.attributes.id
  const analyticResults = React.useRef(new AnalyticResults()).current
  const questionGroups = React.useRef(new QuestionGroups()).current
  const questionCategories = React.useRef(new QuestionCategories()).current
  const [cycle, setCycle] = React.useState<Cycle | null>(null)
  const [requestFilter] = React.useState<AnalyticsFilters>(new AnalyticsFilters(form.id))
  const [reviewAnalyticsFilter, setReviewAnalyticsFilter] = React.useState<
    AnalyticsFilterForm | undefined
  >()

  const { loading } = useAsyncData(async () => {
    getSelectedFilters(reviewAnalyticsFilter)

    const dataAnalytics: DataParams = {
      surveys_form_id: formId,
      reviewee_id: revieweeId,
    }

    const dataQuestionCategories: DataParams = {
      surveys_form_id: formId,
      reviewee_id: revieweeId,
    }

    if (requestFilter.locations.length) {
      dataAnalytics.locations = requestFilter.locations
    }

    if (requestFilter.teams.length) {
      dataAnalytics.teams = requestFilter.teams
    }

    await Promise.all([
      analyticResults.fetch({ data: dataAnalytics }),
      questionCategories.fetch({ data: dataQuestionCategories }),
    ])

    const cyclesArray = Array.from(analyticResults.cycles)
    setCycle(cyclesArray[cyclesArray.length - 1] as Cycle)
  }, [formId, revieweeId, reviewAnalyticsFilter])

  const getSelectedFilters = (reviewAnalyticsFilter: AnalyticsFilterForm | undefined) => {
    if (!reviewAnalyticsFilter?.isActive) {
      // No filter applied
      requestFilter.teams = []
      requestFilter.locations = []
    } else if (reviewAnalyticsFilter?.default.length) {
      // Simple filter applied
      const simpleFilter = reviewAnalyticsFilter.default[0]

      if (simpleFilter?.values.field === 'team') {
        requestFilter.teams = [simpleFilter.values.value]
      } else {
        requestFilter.teams = []
      }

      if (simpleFilter?.values.field === 'location') {
        requestFilter.locations = [simpleFilter.values.value]
      } else {
        requestFilter.locations = []
      }
    } else if (reviewAnalyticsFilter.filters.length) {
      // Advanced filters applied
      requestFilter.teams = []
      requestFilter.locations = []
    }
  }

  const values: Values = {
    loading,
    form,
    analyticResults,
    analyticResult: analyticResults.length ? analyticResults.at(0) : null,
    questionGroups,
    questionCategories,
    cycle,
    setCycle,
    reviewAnalyticsFilter: reviewAnalyticsFilter,
    setReviewAnalyticsFilter: setReviewAnalyticsFilter,
  }

  return <analyticsContext.Provider value={values}>{children}</analyticsContext.Provider>
}

export const useAnalytics = () => {
  const context = React.useContext(analyticsContext)
  if (!context) {
    throw new Error('useAnalytics must be used within ReviewAnalyticsProvider')
  }
  return context
}
